import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import { LanguageProvider } from "./i18n/LanguageContext";
import ClientDashboard from "./pages/ClientDashboard";
import ConsultantDashboard from "./pages/ConsultantDashboard";
import FieldAgentDashboard from "./pages/FieldAgentDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import TicketDetail from "./pages/TicketDetail";
import VideoRoom from "./pages/VideoRoom";
import RemoteSession from "./pages/RemoteSession";
import WorkOrderDetail from "./pages/WorkOrderDetail";
import UserManagement from "./pages/UserManagement";
import QuickBooksPanel from "./pages/QuickBooksPanel";
import PaymentSettings from "./pages/PaymentSettings";
import ProductManagement from "./pages/ProductManagement";
import SLAContractManagement from "./pages/SLAContractManagement";
import TimeTracking from "./pages/TimeTracking";
import ContractorAgreement from "./pages/ContractorAgreement";
import WorkOrderManagement from "./pages/WorkOrderManagement";
import BrandedPortal from "./pages/BrandedPortal";
import ServiceCatalog from "./pages/ServiceCatalog";
import ServiceMarketplace from "./pages/ServiceMarketplace";
import PaymentSuccess from "./pages/PaymentSuccess";
import EarningsDashboard from "./pages/EarningsDashboard";
import InvoiceManagement from "./pages/InvoiceManagement";
import ClientInvoices from "./pages/ClientInvoices";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RemoteJoinGuest from "./pages/RemoteJoinGuest";
import PublicInvoicePay from "./pages/PublicInvoicePay";
import AgreementManagement from "./pages/AgreementManagement";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// API helper with auth
export const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("sag_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("sag_token");
      localStorage.removeItem("sag_user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("sag_user");
    const token = localStorage.getItem("sag_token");
    
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await apiClient.post("/auth/login", { email, password });
    const { token, user: userData } = response.data;
    
    localStorage.setItem("sag_token", token);
    localStorage.setItem("sag_user", JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await apiClient.post("/auth/register", userData);
    const { token, user: newUser } = response.data;
    
    localStorage.setItem("sag_token", token);
    localStorage.setItem("sag_user", JSON.stringify(newUser));
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem("sag_token");
    localStorage.removeItem("sag_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(true);

  useEffect(() => {
    const checkAgreement = async () => {
      if (user && (user.role === "consultant" || user.role === "field_agent")) {
        try {
          const res = await apiClient.get("/contractor-agreement/status");
          setAgreementAccepted(res.data.accepted);
        } catch {
          setAgreementAccepted(false);
        }
      }
      setAgreementChecked(true);
    };
    if (user) checkAgreement();
    else setAgreementChecked(true);
  }, [user]);

  if (loading || !agreementChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-sag-navy font-heading text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    // Preserve the current path so login can redirect back
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} state={{ from: location }} replace />;
  }

  // Agreement gate for contractors
  if ((user.role === "consultant" || user.role === "field_agent") && !agreementAccepted) {
    return <ContractorAgreement onAccepted={() => setAgreementAccepted(true)} />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const dashboardRoutes = {
      admin: "/admin",
      consultant: "/consultant",
      field_agent: "/field-agent",
      client: "/client",
    };
    return <Navigate to={dashboardRoutes[user.role] || "/client"} replace />;
  }

  return children;
};

// Role-based Dashboard Redirect
const DashboardRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-sag-navy font-heading text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const dashboardRoutes = {
    admin: "/admin",
    consultant: "/consultant",
    field_agent: "/field-agent",
    client: "/client",
  };

  return <Navigate to={dashboardRoutes[user.role] || "/client"} replace />;
};

function App() {
  return (
    <LanguageProvider>
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Dashboard Redirect */}
            <Route path="/" element={<DashboardRedirect />} />
            
            {/* Client Routes */}
            <Route
              path="/client"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Consultant Routes */}
            <Route
              path="/consultant"
              element={
                <ProtectedRoute allowedRoles={["consultant", "admin"]}>
                  <ConsultantDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Field Agent Routes */}
            <Route
              path="/field-agent"
              element={
                <ProtectedRoute allowedRoles={["field_agent", "admin"]}>
                  <FieldAgentDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/quickbooks"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <QuickBooksPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payment-settings"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <PaymentSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ProductManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sla-contracts"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <SLAContractManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/time-tracking"
              element={
                <ProtectedRoute allowedRoles={["admin", "consultant"]}>
                  <TimeTracking />
                </ProtectedRoute>
              }
            />
            
            {/* Shared Routes */}
            <Route
              path="/admin/work-orders"
              element={
                <ProtectedRoute allowedRoles={["admin", "consultant"]}>
                  <WorkOrderManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets/:ticketId"
              element={
                <ProtectedRoute>
                  <TicketDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders/:workOrderId"
              element={
                <ProtectedRoute>
                  <WorkOrderDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/video/:roomCode"
              element={
                <ProtectedRoute>
                  <VideoRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/remote/:sessionCode"
              element={
                <ProtectedRoute>
                  <RemoteSession />
                </ProtectedRoute>
              }
            />
            
            {/* Public Branded Portal */}
            <Route path="/portal/:productSlug" element={<BrandedPortal />} />

            {/* Public Remote Session Join (no login required) */}
            <Route path="/remote-join/:sessionCode" element={<RemoteJoinGuest />} />
            <Route path="/pay/:invoice_id" element={<PublicInvoicePay />} />
            
            {/* Payment Routes */}
            <Route path="/admin/services" element={<ProtectedRoute allowedRoles={["admin"]}><ServiceCatalog /></ProtectedRoute>} />
            <Route path="/services" element={<ProtectedRoute><ServiceMarketplace /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/payment/cancel" element={<ProtectedRoute><ServiceMarketplace /></ProtectedRoute>} />
            <Route path="/earnings" element={<ProtectedRoute allowedRoles={["admin", "consultant", "client"]}><EarningsDashboard /></ProtectedRoute>} />
            <Route path="/admin/invoices" element={<ProtectedRoute allowedRoles={["admin"]}><InvoiceManagement /></ProtectedRoute>} />
            <Route path="/admin/agreements" element={<ProtectedRoute allowedRoles={["admin"]}><AgreementManagement /></ProtectedRoute>} />
            <Route path="/client/invoices" element={<ProtectedRoute allowedRoles={["client"]}><ClientInvoices /></ProtectedRoute>} />
            <Route path="/client/invoice/:invoiceId" element={<ProtectedRoute allowedRoles={["client", "admin"]}><InvoiceDetailPage /></ProtectedRoute>} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
